/**
 * OntologySchema - Container for TBox (schema-level) data.
 *
 * Replaces the TBox portion of the deprecated DomainModel class.
 * Contains schema classes, schema individuals, and class hierarchy information.
 */

import SchemaClass from "./SchemaClass.ts";
import SchemaIndividual from "./SchemaIndividual.ts";
import Vocabulary from "@semantical/modules/vocabulary/Vocabulary.ts";
import type { Individual } from "@semantical/modules/semantical-core";
import type { ClassHierarchyMap } from "./types.ts";
import type { PrefixEntry } from "./taxonomyBuilders.ts";
import type { Ontology } from "@semantical/modules/owl2";

/**
 * Prefix map type: namespace IRI → prefix name
 */
export type PrefixMap = Map<string, string>;

export default class OntologySchema {
    name: string;

    /** The unique IRI identifier for this ontology (OWL2 ontologyIRI) */
    ontologyIRI: string;

    /** Optional version-specific IRI (OWL2 versionIRI) */
    versionIRI?: string;

    /** IRIs of imported ontologies (OWL2 owl:imports). Only stores references; actual loading is handled by Workspace. */
    imports: string[] = [];

    /** Prefix declarations from the ontology (namespace → prefix) */
    prefixes: PrefixMap = new Map();

    /**
     * The parsed OWL2 Ontology object.
     * This stores all axioms, declarations, and metadata from the parsed TTL.
     * Use this to query ontology data instead of re-parsing TTL.
     */
    ontology?: Ontology;

    /** Optional source URL of the file from which this ontology was loaded */
    sourceUrl?: string;

    classes: Map<string, SchemaClass> = new Map<string, SchemaClass>();
    individuals: Map<string, SchemaIndividual> = new Map<string, SchemaIndividual>();
    classHierarchy: ClassHierarchyMap | null = null;

    constructor(name: string, ontologyIRI: string, options?: { versionIRI?: string; imports?: string[]; prefixes?: PrefixEntry[]; ontology?: Ontology; sourceUrl?: string }) {
        this.name = name;
        this.ontologyIRI = ontologyIRI;
        this.versionIRI = options?.versionIRI;
        this.imports = options?.imports ?? [];
        this.sourceUrl = options?.sourceUrl;

        // Build prefix map (namespace → prefix)
        if (options?.prefixes) {
            for (const p of options.prefixes) {
                this.prefixes.set(p.namespace, p.prefix);
            }
        }

        // Store the parsed ontology
        if (options?.ontology) {
            this.ontology = options.ontology;
        }
    }

    /**
     * Set the parsed OWL2 Ontology object.
     * Use this when the ontology is parsed separately from schema creation.
     */
    setOntology(ontology: Ontology): void {
        this.ontology = ontology;
    }

    /**
     * Get the namespace URI (base namespace without fragment/local name).
     * Extracts from ontologyIRI by finding the last # or /.
     */
    getNamespace(): string {
        const hashIndex = this.ontologyIRI.lastIndexOf('#');
        const slashIndex = this.ontologyIRI.lastIndexOf('/');
        const separatorIndex = Math.max(hashIndex, slashIndex);
        if (separatorIndex !== -1) {
            return this.ontologyIRI.substring(0, separatorIndex + 1);
        }
        return this.ontologyIRI;
    }

    /**
     * Set the class hierarchy for subclass checking
     */
    setClassHierarchy(hierarchy: ClassHierarchyMap) {
        this.classHierarchy = hierarchy;
    }

    /**
     * Check if a class is equal to or a subclass of a target class
     */
    isSubclassOf(classUri: string, targetUri: string): boolean {
        if (classUri === targetUri) return true;
        if (!this.classHierarchy) return false;

        // Check ancestors recursively with cycle detection
        const visited = new Set<string>();
        const checkAncestors = (uri: string): boolean => {
            if (visited.has(uri)) return false;
            visited.add(uri);

            const parents = this.classHierarchy!.get(uri);
            if (!parents) return false;

            for (const parentUri of parents) {
                if (parentUri === targetUri) return true;
                if (checkAncestors(parentUri)) return true;
            }
            return false;
        };

        return checkAncestors(classUri);
    }

    /**
     * Get a class by its URI
     */
    getClassByUri(uri: string): SchemaClass | undefined {
        return Array.from(this.classes.values()).find(c => c.uri === uri);
    }

    /**
     * Add a class to the schema
     */
    addClass(schemaClass: SchemaClass) {
        // Check if a class with this concept URI already exists
        const existingClass = Array.from(this.classes.values()).find(
            c => c.concept.uri === schemaClass.concept.uri
        );

        if (existingClass) {
            console.warn(`Class with concept URI "${schemaClass.concept.uri}" already exists in the schema. Skipping.`);
            return;
        }

        this.classes.set(schemaClass.name, schemaClass);
    }

    /**
     * Remove a class from the schema
     */
    removeClass(schemaClass: SchemaClass) {
        this.classes.delete(schemaClass.name);
    }

    // =========================================================================
    // Individual Management (TBox-level individuals for enumerations/hasValue)
    // =========================================================================

    /**
     * Add a schema individual (TBox-level individual for enumerations/hasValue)
     */
    addIndividual(individual: SchemaIndividual) {
        this.individuals.set(individual.uri, individual);
    }

    /**
     * Get an individual by its URI.
     * Returns the Individual interface for unified handling with ABox individuals.
     */
    getIndividualByUri(uri: string): Individual | undefined {
        return this.individuals.get(uri);
    }

    /**
     * Get all individuals that are instances of a given class or any of its subclasses.
     * Returns the Individual interface for unified handling with ABox individuals.
     * Useful for populating enumeration pickers in the UI.
     */
    getIndividualsByClass(classUri: string): Individual[] {
        return Array.from(this.individuals.values()).filter(
            individual => {
                // Check each class the individual belongs to
                for (const individualClassUri of individual.classUris) {
                    // Match if the individual's class equals the target or is a subclass of it
                    if (this.isSubclassOf(individualClassUri, classUri)) {
                        return true;
                    }
                }
                return false;
            }
        );
    }

    /**
     * Remove a schema individual
     */
    removeIndividual(individual: SchemaIndividual) {
        this.individuals.delete(individual.uri);
    }

    toJSON() {
        return {
            name: this.name,
            ontologyIRI: this.ontologyIRI,
            versionIRI: this.versionIRI,
            imports: this.imports,
            classes: Array.from(this.classes.values()).map((aClass) => aClass.toJSON()),
            individuals: Array.from(this.individuals.values()).map((individual) => individual.toJSON())
        };
    }

    static fromJSON(json: any, vocabulary: Vocabulary): OntologySchema {
        // Backward compatibility: use name as ontologyIRI fallback if not present
        const ontologyIRI = json.ontologyIRI ?? json.name;

        const schema = new OntologySchema(json.name, ontologyIRI, {
            versionIRI: json.versionIRI,
            imports: json.imports ?? []
        });

        // Restore classes
        (json.classes ?? []).forEach((aClass: any) => {
            schema.addClass(SchemaClass.fromJSON(aClass, vocabulary));
        });

        // Restore schema individuals (TBox-level)
        (json.individuals ?? []).forEach((individual: any) => {
            schema.addIndividual(SchemaIndividual.fromJSON(individual));
        });

        return schema;
    }
}

export { OntologySchema };
